import { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
} from '@artstorefronts/ui';
import { toast } from 'sonner';
import { ADMIN_SCHEDULE_BOUNTY, periodWord, scheduleLabel, type AdminBounty } from './bounty-shared';
import { SeriesReachBackField, canReachBack, reachBackValue, seriesPublishLine } from './SeriesReachBack';

/**
 * SCHEDULE A GO-LIVE (#4118) — the alternative to Go live: pick the day and time a draft
 * goes live. It stays a draft, paying nothing and shown nowhere, until then; the publish job
 * puts it live within five minutes of the time. The server runs every go-live check now.
 */

/** Tomorrow at 9:00 in the admin's time zone, as a datetime-local value. */
function defaultMoment(): string {
  const next = new Date();
  next.setDate(next.getDate() + 1);
  next.setHours(9, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}T09:00`;
}

export function ScheduleBountyDialog({
  bounty,
  emailsEveryone,
  onClose,
  onScheduled,
}: {
  bounty: AdminBounty | null;
  emailsEveryone: boolean;
  onClose: () => void;
  onScheduled: () => void;
}) {
  const [moment, setMoment] = useState(defaultMoment);
  const [reachBack, setReachBack] = useState('');
  const [schedule, { loading }] = useMutation(ADMIN_SCHEDULE_BOUNTY);

  const close = () => {
    setReachBack('');
    setMoment(defaultMoment());
    onClose();
  };

  const handleSchedule = async () => {
    if (!bounty) return;
    const goLiveAt = new Date(moment);
    if (Number.isNaN(goLiveAt.getTime())) {
      toast.error('Pick a date and time to go live.');
      return;
    }
    try {
      await schedule({
        variables: {
          id: bounty.id,
          goLiveAt: goLiveAt.toISOString(),
          reachBackDays: canReachBack(bounty) ? reachBackValue(reachBack) : null,
          confirmEmailBlast: emailsEveryone,
        },
      });
      toast.success(`"${bounty.name}" goes live ${scheduleLabel(goLiveAt.toISOString())}.`);
      close();
      onScheduled();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to schedule');
    }
  };

  return (
    <AlertDialog open={bounty !== null} onOpenChange={(open) => !open && close()}>
      <AlertDialogContent data-testid="bounty-schedule-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Schedule this bounty to go live?</AlertDialogTitle>
          <AlertDialogDescription>
            It stays a draft — paying nothing and shown nowhere — until then, and goes live within five minutes of the
            time you pick.{' '}
            {bounty?.seriesKey
              ? seriesPublishLine(bounty, reachBackValue(reachBack), 'then')
              : bounty
                ? `Then it pays ${bounty.pointsReward} points to each eligible member who finishes it from that moment.`
                : ''}
            {emailsEveryone && bounty
              ? ` It also emails every recently active member each ${periodWord(bounty.cadence)}.`
              : ''}
          </AlertDialogDescription>
          <div className="mt-3 space-y-1 text-left">
            <Label htmlFor="bounty-go-live-at">Goes live at (your time)</Label>
            <Input
              id="bounty-go-live-at"
              type="datetime-local"
              value={moment}
              onChange={(event) => setMoment(event.target.value)}
            />
          </div>
          {bounty && canReachBack(bounty) ? (
            <SeriesReachBackField bounty={bounty} value={reachBack} onChange={setReachBack} />
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button onClick={() => void handleSchedule()} disabled={loading} data-testid="bounty-schedule-confirm">
            {loading ? 'Scheduling…' : 'Schedule'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
